import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { FreewheelBusinessDivision } from 'tag/ts/tag/ads/freewheel/enums/FreewheelBusinessDivision';
import { FreewheelEnvironment } from 'tag/ts/tag/ads/freewheel/enums/FreewheelEnvironment';
import { FreewheelPartner } from 'tag/ts/tag/ads/freewheel/enums/FreewheelPartner';
import { FreewheelPlatform } from 'tag/ts/tag/ads/freewheel/enums/FreewheelPlatform';
import { FreewheelStreamType } from 'tag/ts/tag/ads/freewheel/enums/FreewheelStreamType';
import { ISiteSessionData } from 'tag/ts/tag/interfaces/api/ISiteSessionData';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { FreewheelConstants } from 'tag/ts/tag/model/FreewheelConstants';
import { FreewheelPauseAdConstants } from 'tag/ts/tag/model/FreewheelPauseAdConstants';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { FWUtilities } from 'tag/ts/tag/utilities/FWUtilities';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Base class for all Freewheel classes. Contains references to all data and methods needed across Freewheel classes.
 */
export abstract class AbstractFreewheelClass extends AbstractTagClass {

	protected constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'AbstractFreewheelClass',
				...argument
			}
		);
	}

	/**
	 * @description Map of transformer functions which accept string input and returns url-safe string. Key is input, value is callback function to execute.
	 */
	readonly #toUrlTransformers: { [ value: string ]: ( value: string ) => string } = {
		[ this.fwConstants.randomTag ]: ( value: string ): string => {
			// do not encode "[RANDOM]"
			this?.logger?.log( `toUrlTransformers[${this.fwConstants.randomTag}]`, 'value:', value, ' should not be encoded... returning constant:', this.fwConstants.randomTag );
			return this.fwConstants.randomTag;
		}
	};

	/**
	 * @description Default transformer function to execute when there is no matching function in toUrlTransformers
	 * @param {string} value
	 * @return {string}
	 */
	readonly #defaultToUrlTransformer = ( value: string ): string => {
		const res = encodeURIComponent( ( value.toString() ) );
		this?.logger?.log( 'defaultToUrlTransformer', 'encoding value:', value, '...returning:', res );
		return res;
	};

	/**
	 * @description Returns constant values for Freewheel
	 * @returns {FreewheelConstants}
	 */
	protected get fwConstants(): FreewheelConstants {
		return FreewheelConstants.getInstance();
	}

	/**
	 * @description Returns constant values for Freewheel pause ads
	 * @returns {FreewheelPauseAdConstants}
	 */
	protected get pauseAdConstants(): FreewheelPauseAdConstants {
		return FreewheelPauseAdConstants.getInstance();
	}

	/**
	 * @description Returns instance of FWUtilities singleton.
	 * @return {FWUtilities}
	 */
	protected get fwUtil(): FWUtilities {
		return FWUtilities.getInstance();
	}

	/**
	 * @description Returns siteSessionData
	 * @returns {ISiteSessionData}
	 */
	protected get siteSessionData(): ISiteSessionData {
		return this?.tag?.model?.siteUtilities?.siteSessionData;
	}

	/**
	 * @description Returns embed device (ex: DESKTOP, PS4, ANDROID_PHONE)
	 * @returns {EmbedDevice}
	 */
	protected getSystemDevice(): EmbedDevice {
		return this?.params?.SYSTEM?.device;
	}

	/**
	 * @description Returns embed type (ex: DESKTOP, HTML5)
	 * @returns {string}
	 */
	protected getSystemEmbedType(): EmbedType {
		return this?.params?.SYSTEM?.embedType;
	}

	/**
	 * @description Returns business division value for csid
	 * @returns {string}
	 */
	protected getBusinessDivision(): string {
		if (
			this.isPPlus() ||
			this.isPTV()
		) {
			return FreewheelBusinessDivision.STREAMING;
		}
		if ( this.isCBS() ) {
			return FreewheelBusinessDivision.VCBS;
		}
	}

	/**
	 * @description Returns partner value for csid
	 * @returns {string}
	 */
	protected getPartner(): string {
		if ( this.isPPlus() ) {
			return FreewheelPartner.PARAMOUNT_PLUS;
		}
		if ( this.isCBS() ) {
			return FreewheelPartner.CBS;
		}
		if ( this.isPTV() ) {
			return FreewheelPartner.PLUTO_TV;
		}
		this.logger?.warn( 'getPartner', 'unrecognized domain — no partner matched' );
		return '';
	}

	/**
	 * @description Returns region value from siteSessionData
	 * @returns {string}
	 */
	protected getRegion(): string {
		return this.siteSessionData?.region;
	}

	/**
	 * @description Returns platform value for csid
	 * @returns {string}
	 */
	protected getPlatform(): string {
		if ( this.isDesktop() ) {
			return FreewheelPlatform.DESKTOP;
		} else {
			return FreewheelPlatform.MOBILE;
		}
	}

	/**
	 * @description Returns stream type value for csid
	 * @returns {string}
	 */
	protected getStreamType(): string {
		if ( this.isLive() ) {
			return FreewheelStreamType.LIVE;
		} else {
			return FreewheelStreamType.VOD;
		}
	}

	/**
	 * @description Returns vendor code value for csid
	 * @returns {string}
	 */
	protected getVendorCode(): string {
		return this?.tag?.params?.CONTEXT?.metadata?.vendorCode || ''; // ex: hulu
	}

	/**
	 * @description Generates value for csid Freewheel parameter for Web and HTML5
	 * @returns {string}
	 */
	protected createSiteSectionId( delimiter = '_' ): string {
		let sectionValues: string[];
		switch ( this.getSystemEmbedType() ) {
			case EmbedType.DESKTOP:
			case EmbedType.MOBILE_WEB:
				sectionValues = this.getSectionValuesForWeb();
				break;
			case EmbedType.HTML5:
				sectionValues = this.getSectionValuesForHtml5();
				break;
			default:
				this?.logger?.warn( 'createSiteSectionId', 'unknown EmbedType... default to csid for web', 'EmbedType:', this.getSystemEmbedType() );
				sectionValues = this.getSectionValuesForWeb();
		}
		return sectionValues
			.filter( ( value ) => value?.trim().length > 0 )
			.join( delimiter )
			.toLowerCase();
	}

	/**
	 * @description Returns csid section values for Web
	 * @returns {string[]}
	 */
	protected getSectionValuesForWeb(): string[] {
		const sections = [
			this.getBusinessDivision(),
			this.getPartner(),
			this.getRegion(),
			this.getPlatform(),
			FreewheelEnvironment.WEB,
			this.getStreamType()
		];
		// for P+, append vendorCode after streamType if set
		if (
			this.isPPlus() ||
			this.isCBS()
		) {
			sections.push( this.getVendorCode() );
		}
		 // for PTV, append channelId after streamType if playing live channel
		if (
			this.isPTV() &&
			this.hasStitcherParams() &&
			this.params.STITCHER.streamType?.toLowerCase() === 'live'
		) {
			sections.push( this.params.STITCHER.id );
		}
		return sections;
	}

	/**
	 * @description Returns csid section values for Html5
	 * @returns {string[]}
	 */
	protected getSectionValuesForHtml5(): string[] {
		return [
			this.getBusinessDivision(),
			this.getPartner(),
			this.getCountryCode(), // HTML5 does not receive siteSessionData, default to country
			FreewheelEnvironment.CTV,
			( this.getSystemDevice()?.toLowerCase() || 'unknown' ),
			this.getStreamType()
		];
	}

	/**
	 * @description Create and returns url-safe string of value. Encodes values, except for certain constants not meant for encoding.
	 * @param {string} value
	 * @return {string}
	 */
	protected toUrlParam( value: string ): string {
		if (
			this.utils.Type.isNullOrUndefined( value ) ||
			(
				!this.utils.Type.isString( value ) &&
				!this.utils.Type.isNumber( value ) &&
				!this.utils.Type.isBoolean( value )
			)
		) {
			this?.logger?.warn( 'toUrlParam', 'missing/invalid value... returning value unmodified:', value );
			return value;
		}

		// find appropriate handler
		let handlerToExecute: ( input: string ) => string = this.#toUrlTransformers[ value ];
		if ( this.utils.Type.isNullOrUndefined( handlerToExecute ) ) {
			this?.logger?.log( 'toUrlParam', 'no matching transformers in toUrlTransformers... fallback to defaultToUrlTransformer' );
			handlerToExecute = this.#defaultToUrlTransformer;
		}
		const res = handlerToExecute( value );
		this?.logger?.log( 'toUrlParam', 'value:', value, '...returning url-safe string:', res );
		return res;
	}

	/**
	 * @description Generates value for language_selection Freewheel parameter for Web and HTML5.
	 * @returns {string}
	 */
	protected createLanguageSelection(): string {
		let langSelection = (
			this?.settings?.audioTrack?.language || 		// pass language selection saved in local storage
			this?.siteSessionData?.language_selection || 	// fallback to siteSessionData
			this?.params?.CONTENT?.locale 					// fallback to paramsVO
		);
		// include language code only, no country code
		if ( langSelection?.includes( '-' ) ) {
			langSelection = langSelection.substring( 0, langSelection.indexOf( '-' ) );
		}
		return langSelection;
	}
}